import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import HobbyArt from "./../../../assets/img/giftshop/landing/hobby-art.png";
import {
  isAiSelfieCaricatureHobbyPostPaymentActive,
  shouldUseHobbyCaricatureFlowForStoredAiSelfieArt,
} from "../../../services/stylePromptResolver";
import {
  getPrimaryPreviewUrl,
  navigateToCreatedPersonHobby,
  type HobbyGenerationResult,
} from "../../../utils/hobbyGenerationNavigation";
import {
  isMultiPersonHobbySession,
  saveHobbyPersonResultAndContinue,
} from "../../../utils/hobbyPersonFlow";
import type { RootState } from "../../../redux/store";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
import SignOutConfirmModal from "../../../modal/SignOutConfirmModal";

const HobbyResultReview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showBackConfirmModal, setShowBackConfirmModal] = useState(false);
  const { generationResult, stationData: stationDataFromNav, currentPersonIndex } = (location.state || {}) as {
    generationResult?: HobbyGenerationResult;
    stationData?: Record<string, unknown>;
    currentPersonIndex?: number;
  };

  const stationData = useMemo(() => {
    if (stationDataFromNav && typeof stationDataFromNav === "object") {
      return stationDataFromNav;
    }
    try {
      const selectedRaw = localStorage.getItem("selectedStation");
      const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
      const fromStorage = JSON.parse(localStorage.getItem("stationData") || "{}");
      return fromSelected || fromStorage;
    } catch {
      return {};
    }
  }, [stationDataFromNav]);

  const routeSlug = useMemo(() => {
    if (
      isAiSelfieCaricatureHobbyPostPaymentActive() ||
      shouldUseHobbyCaricatureFlowForStoredAiSelfieArt()
    ) {
      return "hobby-caricature";
    }
    const t = stationData?.type;
    return typeof t === "string" && t.trim() ? t : "hobby-caricature";
  }, [stationData?.type]);

  const hasResult = Boolean(generationResult?.imageUrls?.length);

  // Reload / deep link with no result would leave a dead-end "Do you like this?"
  // screen whose buttons no-op. Send the user back to regenerate instead.
  useEffect(() => {
    if (!hasResult) {
      navigate(`/${routeSlug}/creating-person-hobby`, {
        replace: true,
        state: { stationData, currentPersonIndex },
      });
    }
  }, [hasResult, navigate, routeSlug, stationData, currentPersonIndex]);

  const previewUrl = getPrimaryPreviewUrl(generationResult) || HobbyArt;

  const { hobbyPeopleCount, hobbyPersonCompletedResults } = useSelector((s: RootState) => s.uploadFlow);
  const totalPeople = hobbyPeopleCount ?? 1;
  const currentPersonNumber =
    typeof currentPersonIndex === "number"
      ? currentPersonIndex + 1
      : hobbyPersonCompletedResults.length + 1;
  const isMultiPerson = isMultiPersonHobbySession();

  const handleYes = () => {
    if (!generationResult?.imageUrls?.length) return;
    const selectedUrl = getPrimaryPreviewUrl(generationResult);
    if (!selectedUrl) return;

    if (isMultiPerson) {
      saveHobbyPersonResultAndContinue(navigate, routeSlug, selectedUrl, stationData);
      return;
    }

    navigateToCreatedPersonHobby(navigate, routeSlug, generationResult, stationData, {
      isFinalCombine: true,
    });
  };

  const handleTryMore = () => {
    if (!generationResult?.imageUrls?.length) return;
    navigate(`/${routeSlug}/creating-person-hobby`, {
      state: {
        retryGeneration: true,
        previousGenerationResult: generationResult,
        stationData,
        currentPersonIndex,
      },
    });
  };

  return (
    <>
      <StationThemeLayout
        className="hobbyResultReviewSection p-0"
        station={stationData as { backgroundImageUrl?: string; type?: string; name?: string }}
        showStationLogo
        onBack={() => setShowBackConfirmModal(true)}
      >
        <div className="hobbyResultReviewInner">
          <div className="hobbyResultReviewContent">
            <div className="hobbyResultPreviewCard shadow-sm">
              <img src={previewUrl} alt="Generated hobby caricature" />
            </div>

            <h1 className="hobbyResultReviewHeading mb-4 mt-3 px-4">
              {isMultiPerson ? (
                <>
                  Do you like this result for
                  <br />
                  person {currentPersonNumber} of {totalPeople}?
                </>
              ) : (
                "Do you like this result?"
              )}
            </h1>

            <div className="hobbyResultReviewActions">
              <button type="button" className="hobbyResultBtn hobbyResultBtn--yes" onClick={handleYes}>
                Yes
              </button>
              <button type="button" className="hobbyResultBtn hobbyResultBtn--tryMore" onClick={handleTryMore}>
              Generate one more
              </button>
            </div>
          </div>
        </div>
      </StationThemeLayout>
      {showBackConfirmModal && (
        <SignOutConfirmModal
          show={showBackConfirmModal}
          onHide={() => setShowBackConfirmModal(false)}
          onConfirm={() => {
            setShowBackConfirmModal(false);
            navigate("/");
          }}
          message="Are you sure you want to go back to the home screen?"
          cancelLabel="No"
          confirmLabel="Yes"
        />
      )}
    </>
  );
};

export default HobbyResultReview;
